import { inject, injectable } from "inversify";
import type { IMSelectorsUnion } from "@whatslly/types/integrations/im/IMSelectorsTypes";
import type { IStorage } from "@whatslly/commons/storage.constants";
import { InjectStorage, STORAGE } from "@whatslly/commons/storage.constants";
import { webScriptBackend } from "@whatslly/instant-messaging/webScriptBackend";
import { WebScriptCommands } from "@whatslly/commons/webScriptCommands";
import type { DelegatedDLPResolution, ResolveDLPParams, DLPResolution } from "@whatslly/commons/DLPExecutor";
import { DELEGATE_TO_ASYNC_PHASE, DLPExecutor } from "@whatslly/commons/DLPExecutor";
import { nodeWebDefaultOptions } from "@whatslly/commons/dlpNodeWebUtils";
import { FrontLogger } from "@whatslly/commons/frontLogger/frontLogger";
import type { ListenerService } from "@whatslly/types/instant-messaging/ListenerService";
import { DOM_EVENT_TYPES, IListenerServiceSymbol } from "@whatslly/types/instant-messaging/ListenerService";
import { IMUserStore } from "@whatslly/instant-messaging/IMUserStore";
import { DLPAction } from "@whatslly/types/dlp/DLPPolicy";
import { Direction as DLPDirection, OutboundMessageType as DLPOutboundMessageType } from "@whatslly/types/policy/enums";
import { i18n } from "@whatslly/commons/i18nService";
import { widgetClient } from "@whatslly/commons/widgetClient";
import { WidgetCommands } from "@whatslly/commons/widgetCommands";
import { backgroundClient } from "@whatslly/commons/background/client";
import { BgCommands } from "@whatslly/commons/bgCommands";
import type { TemplateType } from "@whatslly/types/GetTemplatesResponse";
import { DOMInjector } from "./react-app/DOMInjector";
import { LoadingChatOverlay } from "./react-app/BlockWapChat/LoadingChatOverlay";
import type { InstantMessagingService } from "@whatslly/types/instant-messaging/InstantMessagingService";
import { IInstantMessagingServiceSymbol } from "@whatslly/types/instant-messaging/InstantMessagingService";
import dayjs from "dayjs";
import type { UserType } from "@whatslly/types/User";
import type { IImTemplatesStore } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { ImTemplatesStoreSymbol, TEMPLATE_DATA_TRANSFER_TYPE } from "@whatslly/instant-messaging/commons/IImTemplatesStore";
import { reaction } from "mobx";
import { ContactService } from "@whatslly/instant-messaging/contactService";
import type { ISendMessage } from "@whatslly/instant-messaging/sendMessage";
import { InjectSendMessage } from "@whatslly/instant-messaging/sendMessage";
import { ChunkedQueue } from "@whatslly/commons/chunkedQueue";
import type { AnalyzeDLPJob } from "@whatslly/types/dlp/DLPAnalyzeJob";
const SCANNING_ATTACHMENT_NAMESPACE = "SCANNING_ATTACHMENT_NAMESPACE";
import { type IMessageEmitter, InjectMessageEmitter } from "@whatslly/types/instant-messaging/messagesEmitter";
import type { ImChat } from "@whatslly/types/tuvis-messaging/TuvisMessagingChats";
import { IncidentsChannel } from "@whatslly/types/incident/IncidentsChannels";

function base64ToFile(base64: string, name: string, type: string): File {
  const byteString = atob(base64.includes(",") ? base64.split(",")[1] : base64);
  const bytes = new Uint8Array(byteString.length);
  for (let i = 0; i < byteString.length; i++) bytes[i] = byteString.charCodeAt(i);
  return new File([bytes], name, { type });
}

export const InjectBlockMessaging = Symbol("InjectBlockMessaging");
export interface IBlockMessaging<TSelectors extends IMSelectorsUnion = IMSelectorsUnion> {
  init(selectors: TSelectors): Promise<void>;
}

type ScanningKind = "text" | "attachment" | "event" | "order" | "poll" | "contact";

@injectable()
export abstract class BaseBlockMessagingService<TSelectors extends IMSelectorsUnion = IMSelectorsUnion> implements IBlockMessaging<TSelectors> {
  protected selectors?: TSelectors;
  channelType?: IncidentsChannel;
  protected logger: FrontLogger;
  #dlpExecutor: DLPExecutor | undefined = undefined;
  #contactsMap: Record<string, string[]> = {};
  #scanningKindByPhone: Record<string, ScanningKind> = {};
  protected jobQueue = new ChunkedQueue<AnalyzeDLPJob>(async (jobs) => this.processJobs(jobs), { chunkSize: 1, interval: 0 });
  private userHasRelatedDlpPolicies: boolean = false;
  constructor(
    @inject(FrontLogger) frontLogger: FrontLogger,
    @inject(InjectStorage) private readonly storage: IStorage,
    @inject(ImTemplatesStoreSymbol) private readonly templatesStore: IImTemplatesStore,
    @inject(IListenerServiceSymbol) private readonly domListenerService: ListenerService,
    @inject(IMUserStore) protected readonly imUserStore: IMUserStore,
    @inject(InjectSendMessage) protected readonly sendMessage: ISendMessage,
    @inject(IInstantMessagingServiceSymbol) protected readonly instantMessagingService: InstantMessagingService,
    @inject(ContactService) private readonly contactService: ContactService,
    @inject(InjectMessageEmitter) private readonly messageEmitter: IMessageEmitter<any, any>
  ) {
    this.logger = frontLogger.getLogger("block-attach-file-service");
    reaction(
      () => this.imUserStore.user,
      () => this.injectAllClassSettings()
    );
  }

  reset() {
    this.#dlpExecutor = undefined;
    this.jobQueue.clear();
  }

  private injectBlockChannelsClass() {
    this.toggleClassByUserSetting({
      userSettingCondition: this.imUserStore.user?.hide_wap_channels_button_in_desktop,
      className: "tuvis-hide-channels-button",
    });
  }

  protected abstract handleShowOnlyChannelsClass(): void;

  private initCopyPasteMonitor() {
    if (this.imUserStore.user?.block_copy_paste) {
      backgroundClient.send(BgCommands.START_COPY_MONITOR, { blockCopy: this.imUserStore.user.block_copy_paste });
      webScriptBackend.on(WebScriptCommands.ON_COPY, (text: string) => {
        widgetClient.send(WidgetCommands.OPEN_TOAST, { text: i18n.t("dlp.copy_blocked", { policy: text }), type: "error" });
      });
    }
  }

  private injectShowOnlyChannelsClass() {
    this.toggleClassByUserSetting({
      userSettingCondition: this.imUserStore.user?.show_only_wap_channels_in_desktop,
      className: "tuvis-show-only-channels",
    });

    if (this.imUserStore.user?.show_only_wap_channels_in_desktop) {
      this.handleShowOnlyChannelsClass();
    }
  }

  private injectBlockMetaAiClass() {
    this.toggleClassByUserSetting({
      userSettingCondition: this.imUserStore.user?.hide_meta_ai_button_in_desktop,
      className: "tuvis-hide-meta-ai-button",
    });
  }
  private injectBlockForwardClass() {
    this.toggleClassByUserSetting({ userSettingCondition: this.imUserStore.user?.hide_forward_button, className: "tuvis-hide-forward" });
  }

  private injectBlockPersonClass() {
    this.toggleClassByUserSetting({
      userSettingCondition: this.imUserStore.user?.hide_block_contact_button,
      className: "tuvis-block-person-button",
    });
  }

  private injectDeleteConversationClass() {
    this.toggleClassByUserSetting({
      userSettingCondition: this.imUserStore.user?.hide_delete_conversation_button,
      className: "tuvis-delete-conversation-button",
    });
  }

  private toggleClassByUserSetting({ userSettingCondition, className }: { userSettingCondition: boolean | undefined; className: string }) {
    if (!this.selectors) {
      return;
    }

    const element = document.querySelector<HTMLDivElement>(this.selectors.css.app);
    if (!element) {
      return;
    }

    if (!userSettingCondition) {
      element.classList.remove(className);
      return;
    }

    element.classList.add(className);
  }

  private injectAllClassSettings() {
    this.injectBlockForwardClass();
    this.injectBlockPersonClass();
    this.injectDeleteConversationClass();
    this.injectBlockChannelsClass();
    this.injectBlockMetaAiClass();
    this.injectShowOnlyChannelsClass();
    this.initCopyPasteMonitor();
  }

  private injectOverlay(text: string) {
    if (document.querySelector("#whatsly-dlp-overlay")) return;
    const host = document.querySelector<HTMLDivElement>(this.selectors?.css.footer || "#main footer");
    if (!host) return;
    host.parentElement?.style.setProperty("z-index", "1000");
    const props = { children: text };
    DOMInjector.injectToDOM({ host, Component: LoadingChatOverlay, namespace: SCANNING_ATTACHMENT_NAMESPACE, props });
    const contenteditable = document.querySelector<HTMLDivElement>(this.selectors?.css.copyableText || "[contenteditable='true']");
    if (contenteditable) contenteditable.contentEditable = "false";
  }

  private ejectOverlay() {
    const overlay = document.querySelector<HTMLDivElement>("#whatsly-dlp-overlay");
    const container = overlay?.querySelector<HTMLDivElement>("[data-loader='true']");
    const textCollapseDurationMs = 400;
    const overlayFadeDurationMs = 300;

    const cleanup = () => {
      DOMInjector.detachByNamespace(SCANNING_ATTACHMENT_NAMESPACE);
      const host = document.querySelector<HTMLDivElement>(this.selectors?.css.footer || "#main footer");
      host?.parentElement?.style.removeProperty("z-index");
      const contenteditable = document.querySelector<HTMLDivElement>(this.selectors?.css.copyableTextUneditable || "[contenteditable='false']");
      if (contenteditable) contenteditable.contentEditable = "true";
    };

    let isCleanedUp = false;
    const cleanupOnce = () => {
      if (isCleanedUp) {
        return;
      }
      isCleanedUp = true;
      cleanup();
    };

    if (!overlay || !container) {
      return cleanupOnce();
    }

    const startOverlayFade = () => {
      overlay.setAttribute("data-show", "false");
      requestAnimationFrame(() => {
        overlay.style.opacity = "0";
      });
      window.setTimeout(cleanupOnce, overlayFadeDurationMs);
    };

    if (!Array.from(container.classList).some((cls) => cls.includes("loadingTextVisible"))) {
      startOverlayFade();
      return;
    }

    Array.from(container.classList)
      .filter((cls) => cls.includes("loadingTextVisible"))
      .forEach((cls) => container.classList.remove(cls));
    window.setTimeout(startOverlayFade, textCollapseDurationMs);
  }

  private refreshScanOverlayForCurrentChat() {
    const kind = this.#scanningKindByPhone[this.instantMessagingService.getSelectedContact?.()?.phone ?? ""];
    if (!kind) return this.ejectOverlay();
    this.injectOverlay(i18n.t(kind === "attachment" ? "dlp.scanning_attachment" : "dlp.scanning_message"));
  }

  private onScanProcessingStart(phone: string | undefined, kind: ScanningKind) {
    if (!phone) return;
    this.#scanningKindByPhone[phone] = kind;
    this.injectOverlay(i18n.t(kind === "attachment" ? "dlp.scanning_attachment" : "dlp.scanning_message"));
  }

  private onScanProcessingEnd(phone: string | undefined) {
    if (!phone) return;
    if (this.#scanningKindByPhone[phone]) delete this.#scanningKindByPhone[phone];
    this.refreshScanOverlayForCurrentChat();
  }

  private async processJobs(jobs: AnalyzeDLPJob[]) {
    for (const job of jobs) {
      const shouldScan = job.shouldScan && this.hasDLPPolicies;
      if (shouldScan) this.onScanProcessingStart(job.phone, job.kind);
      try {
        const blocked =
          shouldScan &&
          (job.kind !== "attachment"
            ? await this.scanTextMessage(job.message, job.phone, job.kind as DLPOutboundMessageType)
            : await this.scanAttachmentMessage(job.file, job.caption, job.mimeType, job.phone));
        if (blocked) continue;
        if (job.executeOriginal) {
          try {
            await job.executeOriginal();
            continue;
          } catch (err) {
            this.logger.warn({ err }, "Failed to use original send method, falling back to UI send");
          }
        }

        const needsSwitch = Boolean(job.phone && job.phone !== this.instantMessagingService.getSelectedContact?.()?.phone);
        if (needsSwitch && job.phone) await this.instantMessagingService.openChat?.(job.phone, true);
        await this.sendMessage.sendMessage(
          job.kind === "attachment" ? (job.caption ?? "") : job.message,
          job.kind === "attachment" ? [job.file] : undefined
        );
      } catch (err) {
        this.logger.error({ err, jobKind: job.kind }, "Error processing outbound job");
      } finally {
        if (shouldScan) this.onScanProcessingEnd(job.phone);
      }
    }
  }

  private async getDLPPolicies() {
    const { dlpRules, dlpSettings } = await this.storage.get([STORAGE.DLP_SETTINGS, STORAGE.DLP_RULES]);
    const options = {
      ...nodeWebDefaultOptions,
      onShouldRefreshPolicies: () => {
        backgroundClient.send(BgCommands.REFRESH_DLP_POLICIES).catch(() => {});
      },
    };
    this.#dlpExecutor =
      dlpSettings?.policies.length || dlpSettings?.hasDlpDetector
        ? new DLPExecutor(dlpSettings, options)
        : dlpRules && new DLPExecutor({ ...(dlpSettings ?? ({} as any)), policies: dlpRules }, options);
    this.userHasRelatedDlpPolicies = this.#dlpExecutor?.userHasRelatedPolicy(this.imUserStore.user);
  }

  private async getEngagedContacts() {
    this.#contactsMap = (await this.storage.get(STORAGE.ENGAGED_CONTACTS_MAP)) ?? {};
  }

  private getChannelType(): IncidentsChannel {
    if (this.channelType) return this.channelType;
    const activeChat = this.messageEmitter.getActiveChat();
    if (activeChat) return this.formatChannelType(activeChat.type);
    return IncidentsChannel.WHATSAPP_API;
  }

  private formatChannelType(channelType: ImChat["type"]): IncidentsChannel {
    if (channelType === "whatsapp") return IncidentsChannel.WHATSAPP_API;
    if (channelType === "twilio") return IncidentsChannel.SMS;
    return channelType;
  }

  private resolveDLPUpload({
    silent = true,
    isTemplate,
    mimeType,
  }: Partial<Omit<ResolveDLPParams, "user" | "direction" | "content" | "fileSize">> & { silent?: boolean }) {
    const { user } = this.imUserStore;
    const channel = this.getChannelType();

    const result = this.#dlpExecutor?.resolveUploadSync({ direction: DLPDirection.UPLOAD, user, mimeType, isTemplate, channel }, silent);
    if (result?.resolution === DELEGATE_TO_ASYNC_PHASE) {
      this.logger.info("Can not determine if should block upload, waiting to delegate to send phase");
    }
    return result as DLPResolution | undefined;
  }
  public shouldBlockUploadSync(props: Parameters<BaseBlockMessagingService["resolveDLPUpload"]>[0]) {
    return this.resolveDLPUpload(props)?.resolution === DLPAction.BLOCK;
  }

  private async getTemplates() {
    if (!this.#dlpExecutor?.hasTemplateCondition) return { templates: [] as TemplateType[] };
    return await this.templatesStore.getTemplates().catch(() => ({ templates: [] as TemplateType[] }));
  }

  protected shouldLimitUserEngagement(user: UserType) {
    const msgs = this.instantMessagingService.getAllMessages?.().toSorted((a, b) => a.t! - b.t!);
    if (user?.enable_limit_number_of_msgs_to_unresponsive_contact && user.limit_number_of_msgs_to_unresponsive_contact) {
      const lastXMsgs = msgs?.slice(-user.limit_number_of_msgs_to_unresponsive_contact);
      if (lastXMsgs?.length === user.limit_number_of_msgs_to_unresponsive_contact && lastXMsgs?.every((msg) => msg.isSentByMe)) {
        widgetClient.send(WidgetCommands.OPEN_TOAST, { text: i18n.t("limit_number_of_msgs_to_unresponsive_contact"), type: "error" });
        return true;
      }
    }
    if (user?.enable_limit_number_of_contacts_engaged && user.limit_number_of_contacts_engaged) {
      if (msgs?.some((msg) => !msg.isSentByMe)) {
        return false;
      }
      let contactsMap = this.#contactsMap;
      const selectedContact = this.instantMessagingService.getSelectedContact?.();
      if (!selectedContact) {
        return;
      }
      const today = dayjs().format("DD_MM_YY");
      contactsMap = { [today]: [...(contactsMap[today] ?? [])] };
      if (!contactsMap[today]) {
        contactsMap[today] = [selectedContact.phone!];
      } else if (!contactsMap[today].includes(selectedContact.phone!)) {
        if (contactsMap[today].length < user.limit_number_of_contacts_engaged) {
          contactsMap[today].push(selectedContact.phone!);
        } else {
          widgetClient.send(WidgetCommands.OPEN_TOAST, { text: i18n.t("limit_number_of_contacts_engaged"), type: "error" });
          return true;
        }
      }

      this.storage.set(STORAGE.ENGAGED_CONTACTS_MAP, contactsMap);
    }
    return false;
  }

  public async scanAttachmentMessage(content: File, caption?: string, mimeType?: string, phone?: string) {
    const { user } = this.imUserStore;
    if (user && this.shouldLimitUserEngagement(user)) return true;
    if (!this.hasDLPPolicies || !this.shouldScanCurrentContact() || (!content && !caption)) return false;
    try {
      const fileMimeType = (content?.type || mimeType) ?? "";
      const selectedContact = this.instantMessagingService.getSelectedContact?.();
      const templates = await this.getTemplates();
      const isTemplate = templates.templates.some(
        ({ msg, Id }) => (!caption || msg.trim() === caption) && this.templatesStore.templateFilesCache.get(Id)?.includes(content)
      );
      let result = await this.checkDLP({
        direction: DLPDirection.UPLOAD,
        channel: this.getChannelType(),
        user,
        isTemplate,
        content,
        mimeType: fileMimeType,
        contactPhone: selectedContact?.phone,
        isGroup: selectedContact?.isGroup,
      });
      if (result?.resolution !== DLPAction.BLOCK && caption?.length) {
        result = await this.checkDLP({
          direction: DLPDirection.OUTBOUND,
          channel: this.getChannelType(),
          user,
          isTemplate,
          content: caption,
          mimeType: fileMimeType,
          contactPhone: selectedContact?.phone,
          isGroup: selectedContact?.isGroup,
        });
      }
      if (result?.resolution === DLPAction.BLOCK) {
        let baseText: string;
        if (result.isCorruptedOrEncryptedFile) {
          baseText = i18n.t("dlp.file_corrupted_or_encrypted_file_blocked");
        } else if (result.isAudioTranscriptionFailed) {
          baseText = i18n.t("dlp.audio_transcription_failed_blocked");
        } else {
          baseText = i18n.t("dlp.attachment_blocked", { policy: result?.policy?.name });
        }
        const text = phone ? `${baseText} (${phone})` : baseText;
        widgetClient.send(WidgetCommands.OPEN_TOAST, {
          text,
          type: "error",
        });
        return true;
      }
    } catch (err) {
      this.logger.error({ err }, "Error scanning attachment message");
      throw err;
    }

    return false;
  }

  public async scanTextMessage(msg: string, phone: string | undefined, msgType: DLPOutboundMessageType) {
    const { user } = this.imUserStore;
    if (user && this.shouldLimitUserEngagement(user)) return true;
    if (!this.hasDLPPolicies || !this.shouldScanCurrentContact()) return false;
    try {
      const selectedContact = this.instantMessagingService.getSelectedContact?.();
      const templates = await this.getTemplates();
      const isTemplate = templates.templates.some((template) => template.msg.trim() === msg.trim() && !template.templateFiles?.length);
      const result = await this.checkDLP({
        direction: DLPDirection.OUTBOUND,
        user,
        isTemplate,
        content: msg,
        channel: this.getChannelType(),
        contactPhone: selectedContact?.phone,
        isGroup: selectedContact?.isGroup,
        msgType,
      });
      if (result?.resolution === DLPAction.BLOCK) {
        const baseText = i18n.t("dlp.message_blocked", { policy: result.policy?.name });
        const text = phone ? `${baseText} (${phone})` : baseText;
        widgetClient.send(WidgetCommands.OPEN_TOAST, { text, type: "error" });
        return true;
      }
    } catch (err) {
      this.logger.error({ err }, "Error scanning text message");
      throw err;
    }
    return false;
  }

  shouldScanCurrentContact() {
    return this.contactService.isWhitelisted && this.hasDLPPolicies && this.userHasRelatedDlpPolicies;
  }

  shouldBlockPasteEvent = (event: ClipboardEvent) => {
    // this shouldBlockPasteEvent cannot be async function because event.preventDefault(); and event.stopPropagation(); don't work if they don't happen synchronously
    const { user } = this.imUserStore;
    if (user && this.shouldLimitUserEngagement(user)) return true;
    const hasBlockPasteFlag = this.imUserStore.user?.block_copy_paste;
    const isTemplate = Boolean(event.clipboardData?.getData(TEMPLATE_DATA_TRANSFER_TYPE));
    const hasFiles = event?.clipboardData?.files && event.clipboardData.files.length > 0;
    const hasText = event?.clipboardData && event?.clipboardData?.getData("text/plain")?.length > 0;
    if (!hasFiles && !hasBlockPasteFlag) {
      return false;
    }
    const results = Array.from(event.clipboardData?.items ?? [])
      .map((item) => this.resolveDLPUpload({ mimeType: item.type, isTemplate, silent: false }))
      .filter(Boolean);
    const blockedResults = results.filter((result) => result?.resolution === DLPAction.BLOCK);
    const blockedResultsCorruptedOrEncryptedFile = blockedResults.some((result) => result?.isCorruptedOrEncryptedFile);
    const shouldBlock = blockedResults.length > 0 || (hasBlockPasteFlag && hasText);
    if (shouldBlock) {
      let text = blockedResultsCorruptedOrEncryptedFile
        ? i18n.t("dlp.file_corrupted_or_encrypted_file_blocked")
        : i18n.t("dlp.attachment_blocked", { policy: [...new Set(blockedResults.map((result) => result?.policy?.name))].join(", ") });

      if (hasBlockPasteFlag && blockedResults.length === 0) text = i18n.t("dlp.paste_blocked");
      widgetClient.send(WidgetCommands.OPEN_TOAST, { text, type: "error" });
      event.stopPropagation();
      event.preventDefault();
    }
    return shouldBlock;
  };

  addEventListeners() {
    let dlpResoution: DelegatedDLPResolution | undefined;
    const shouldBlock = (event: DragEvent) => {
      if (!event.dataTransfer?.items || event.dataTransfer.items.length === 0) {
        return false;
      }
      const isTemplate = Boolean(event.dataTransfer?.getData(TEMPLATE_DATA_TRANSFER_TYPE));
      for (const item of event.dataTransfer.items) {
        dlpResoution = this.resolveDLPUpload({ mimeType: item.type, isTemplate });
        if (dlpResoution?.resolution === DLPAction.BLOCK) {
          return true;
        }
      }
    };
    const handleDrag = (event: DragEvent) => {
      if (shouldBlock(event)) {
        event.stopPropagation();
        event.preventDefault();
      }
    };
    const handleDrop = (event: DragEvent) => {
      // this handleDrop cannot be async function because event.preventDefault(); and event.stopPropagation(); don't work if they don't happen synchronously
      const { user } = this.imUserStore;
      if (user && this.shouldLimitUserEngagement(user)) return true;

      if (dlpResoution?.resolution === DLPAction.BLOCK) {
        // Run DLP Again with silent = false, to notify if blocked
        widgetClient.send(WidgetCommands.OPEN_TOAST, {
          text: i18n.t("dlp.attachment_blocked", { policy: dlpResoution.policy?.name }),
          type: "error",
        });
        const isTemplate = Boolean(event.dataTransfer?.getData(TEMPLATE_DATA_TRANSFER_TYPE));
        Array.from(event.dataTransfer?.items ?? []).forEach((item) => this.shouldBlockUploadSync({ mimeType: item.type, isTemplate, silent: false }));
        event.stopPropagation();
        event.preventDefault();
      }
      dlpResoution = undefined;
    };
    document.addEventListener("dragenter", handleDrag, true);
    document.addEventListener("drop", handleDrop, true);
  }

  private async checkDLP(params: ResolveDLPParams): Promise<DLPResolution | undefined> {
    if (!this.hasDLPPolicies || !this.userHasRelatedDlpPolicies) return undefined;
    return this.#dlpExecutor?.resolve(params);
  }

  get hasDLPPolicies() {
    return Boolean(this.#dlpExecutor?.hasPolicies);
  }

  abstract onContactChanged(): Promise<void>;
  async init(selectors: TSelectors) {
    this.selectors = selectors;
    webScriptBackend
      .on(WebScriptCommands.LOGOUT, () => this.reset())
      .on([WebScriptCommands.USER_REFRESHED, WebScriptCommands.AUTH], async () => {
        await this.getDLPPolicies();
        this.onContactChanged();
      })
      .on(WebScriptCommands.DLP_POLICIES_CHANGED, async () => {
        await this.getDLPPolicies();
      })
      .on(WebScriptCommands.CHECK_DLP, async ({ content, attachments }) => {
        if (attachments?.length) {
          for (const att of attachments) {
            const file = base64ToFile(att.data, att.name, att.type);
            const result = await this.checkDLP({
              direction: DLPDirection.UPLOAD,
              user: this.imUserStore.user,
              content: file,
              mimeType: att.type,
              channel: this.getChannelType(),
            });
            if (result?.resolution === DLPAction.BLOCK) {
              return {
                blocked: true,
                policyName: result.policy?.name,
                isCorruptedOrEncryptedFile: result.isCorruptedOrEncryptedFile,
                isAudioTranscriptionFailed: result.isAudioTranscriptionFailed,
              };
            }
          }
        }
        if (content) {
          const result = await this.checkDLP({
            direction: DLPDirection.OUTBOUND,
            user: this.imUserStore.user,
            content,
            channel: this.getChannelType(),
          });
          if (result?.resolution === DLPAction.BLOCK) {
            return {
              blocked: true,
              policyName: result.policy?.name,
              isCorruptedOrEncryptedFile: result.isCorruptedOrEncryptedFile,
              isAudioTranscriptionFailed: result.isAudioTranscriptionFailed,
            };
          }
        }
        return { blocked: false };
      });

    this.addEventListeners();
    this.domListenerService.on(DOM_EVENT_TYPES.CONTACT_CHANGED, () => {
      this.onContactChanged();
      this.getEngagedContacts();
      this.refreshScanOverlayForCurrentChat();
    });
    await this.getDLPPolicies();
  }
}
